import { Button } from "primereact/button";
import { InputNumber } from "primereact/inputnumber";
import { InputText } from "primereact/inputtext";
import { Message } from "primereact/message";
import { Tag } from "primereact/tag";
import { useState } from "react";
import { useRecordBenefitVerdictMutation } from "store/queries/benefitComparison";
import {
  ComparisonBenefit,
  IBenefitVerdict,
  IRecordVerdictRequest,
  IVerdictOption,
} from "types/BenefitComparison/BenefitComparison";

import {
  COMPARISON_BENEFIT_LABELS,
  formatComparisonValue,
  VERDICT_QUESTION,
  verdictOptionLabel,
} from "./benefitComparisonWording";

/**
 * CLE-2820 PR 4 — recording ONE verdict: what the estimate should have quoted for this check × benefit.
 *
 * 🚨 THE REVIEWER RECORDS A VALUE, NOT A WINNER. There is no "Legacy was right" button: the buttons are
 * the DISTINCT values, labelled with whoever happens to have picked each, and the value is what is
 * stored. That is what keeps a verdict true after the engine's configuration changes.
 *
 * 🚨 NO BULK VERDICT. One check at a time, deliberately (the ticket defers bulk until single verdicts
 * prove reliable) — a bulk mistake spreads across a whole cluster.
 *
 * Keyboard: every control is a real button or input in document order, so Tab reaches each option and
 * Enter or Space records it; the "None of these" amount submits on Enter.
 */
export function BenefitVerdictPanel({
  pverifyResponseId,
  benefit,
  options,
  verdict,
  remitValue,
  remitConflict,
  compact = false,
}: {
  pverifyResponseId: string;
  benefit: ComparisonBenefit;
  options: IVerdictOption[];
  verdict: IBenefitVerdict | null;
  remitValue?: number | null;
  remitConflict?: boolean;
  compact?: boolean;
}) {
  const [record, { isLoading, isError }] = useRecordBenefitVerdictMutation();
  const [other, setOther] = useState<number | null>(null);
  const [note, setNote] = useState("");
  const [changing, setChanging] = useState(false);

  const save = async (body: IRecordVerdictRequest) => {
    await record(body);
    setChanging(false);
    setOther(null);
    setNote("");
  };

  const chooseValue = (value: number) =>
    save({
      pverifyResponseId,
      benefit,
      verdict: "VALUE",
      value,
      ...(note.trim() ? { note: note.trim() } : {}),
    });

  const isChosen = (option: IVerdictOption) =>
    verdict?.verdict === "VALUE" &&
    verdict.value !== null &&
    Math.abs(verdict.value - option.value) < 0.005;

  const showForm = !verdict || changing;

  return (
    <div
      className="flex flex-column gap-2"
      data-testid={`benefitVerdict.${pverifyResponseId}.${benefit}`}
    >
      {!compact && (
        <span className="font-semibold">
          {COMPARISON_BENEFIT_LABELS[benefit]}
        </span>
      )}
      <span className="text-color-secondary">{VERDICT_QUESTION}</span>

      {verdict && (
        <div
          className="flex flex-wrap align-items-center gap-2"
          data-testid="benefitVerdict.current"
        >
          <Tag
            severity={verdict.verdict === "VALUE" ? "success" : "warning"}
            value={
              verdict.verdict === "VALUE"
                ? `Verdict ${formatComparisonValue(verdict.value, benefit)}`
                : "Verdict: can’t tell from this response"
            }
          />
          <span className="text-sm text-color-secondary">
            {verdict.reviewedByName ?? "Unknown reviewer"} ·{" "}
            {verdict.recordedAt.slice(0, 10)}
            {verdict.revisions > 1
              ? ` · changed ${verdict.revisions - 1}×`
              : ""}
          </span>
          {!changing && (
            <Button
              type="button"
              label="Change"
              className="p-button-text p-button-sm"
              data-testid="benefitVerdict.change"
              onClick={() => setChanging(true)}
            />
          )}
        </div>
      )}

      {/* The reason, in the reviewer's own words — what tells the next reader why the verdict says this. */}
      {verdict?.note && (
        <span
          className="text-sm font-italic"
          data-testid="benefitVerdict.currentNote"
        >
          “{verdict.note}”
        </span>
      )}

      {/* 🚨 TRUTH ORDER: a remit OUTRANKS a verdict. Both are shown; neither is overwritten or hidden. */}
      {remitConflict && (
        <Message
          severity="warn"
          data-testid="benefitVerdict.remitConflict"
          text={`The remit says ${formatComparisonValue(
            remitValue ?? null,
            benefit
          )}, which is not what this verdict says. A remit outranks a verdict — but remits can be wrong too, so both are kept.`}
        />
      )}

      {showForm && (
        <div className="flex flex-wrap align-items-center gap-2">
          {options.map((option) => (
            <Button
              key={`${option.value}-${option.sides.join("")}`}
              type="button"
              label={verdictOptionLabel(option, benefit)}
              className={
                isChosen(option)
                  ? "p-button-sm"
                  : "p-button-outlined p-button-sm"
              }
              disabled={isLoading}
              data-testid={`benefitVerdict.option.${option.sides.join("-")}`}
              onClick={() => chooseValue(option.value)}
            />
          ))}
          {options.length === 0 && (
            <span className="text-color-secondary">
              No side picked a value for this check.
            </span>
          )}
          <span className="flex align-items-center gap-1">
            <InputNumber
              value={other}
              onValueChange={(event) => setOther(event.value ?? null)}
              placeholder="None of these"
              mode={benefit === "COINSURANCE_PERCENT" ? "decimal" : "currency"}
              currency="USD"
              locale="en-US"
              suffix={benefit === "COINSURANCE_PERCENT" ? "%" : undefined}
              inputClassName="w-8rem"
              data-testid="benefitVerdict.other"
              onKeyDown={(event) => {
                if (event.key === "Enter" && other !== null) chooseValue(other);
              }}
            />
            <Button
              type="button"
              label="Use this value"
              className="p-button-outlined p-button-sm"
              disabled={other === null || isLoading}
              data-testid="benefitVerdict.useOther"
              onClick={() => other !== null && chooseValue(other)}
            />
          </span>
          <Button
            type="button"
            label="Can’t tell from this response"
            className="p-button-outlined p-button-sm p-button-secondary"
            disabled={isLoading}
            data-testid="benefitVerdict.cannotDetermine"
            onClick={() =>
              save({
                pverifyResponseId,
                benefit,
                verdict: "CANNOT_DETERMINE",
                ...(note.trim() ? { note: note.trim() } : {}),
              })
            }
          />
          {changing && (
            <Button
              type="button"
              label="Cancel"
              className="p-button-text p-button-sm"
              onClick={() => setChanging(false)}
            />
          )}
        </div>
      )}

      {showForm && (
        <InputText
          value={note}
          onChange={(event) => setNote(event.target.value)}
          placeholder="Why (optional)"
          className="w-full"
          data-testid="benefitVerdict.note"
        />
      )}

      {isError && (
        <Message severity="error" text="That verdict could not be saved." />
      )}

      {!compact && (
        <span className="text-xs text-color-secondary">
          A verdict records the correct <strong>value</strong>, never which
          method was right — so it stays true after the engine&apos;s
          configuration or code changes.
        </span>
      )}
    </div>
  );
}
